import { remoteConfig } from "@/firebase";
import { VERSION_CONFIG } from "@/config/version";

/**
 * Compare two semantic version strings
 * @param {string} currentVersion - Current app version (e.g., "0.1.0")
 * @param {string} minimumVersion - Minimum required version (e.g., "0.2.0")
 * @returns {boolean} - True if current version is less than minimum version
 */
export function isVersionOutdated(currentVersion, minimumVersion) {
  try {
    const current = currentVersion.split(".").map((part) => {
      const num = parseInt(part, 10);
      return isNaN(num) ? 0 : num;
    });
    const minimum = minimumVersion.split(".").map((part) => {
      const num = parseInt(part, 10);
      return isNaN(num) ? 0 : num;
    });

    const maxLength = Math.max(current.length, minimum.length);
    for (let i = 0; i < maxLength; i++) {
      const currentPart = current[i] || 0;
      const minimumPart = minimum[i] || 0;

      if (currentPart < minimumPart) return true;
      if (currentPart > minimumPart) return false;
    }
    return false; // Versions are equal
  } catch (error) {
    console.error("Error comparing versions:", error);
    return false;
  }
}

/**
 * Check if app version needs update based on Firebase Remote Config
 * @returns {Promise<{needsUpdate: boolean, forceUpdate: boolean, minimumVersion: string}>}
 */
export async function checkAppVersion() {
  try {
    // Fetch and activate remote config
    await remoteConfig.fetchAndActivate();

    const minimumVersion = remoteConfig
      .getValue(VERSION_CONFIG.REMOTE_CONFIG_KEYS.MINIMUM_VERSION)
      .asString();
    const forceUpdate =
      remoteConfig
        .getValue(VERSION_CONFIG.REMOTE_CONFIG_KEYS.FORCE_UPDATE)
        .asString() === "true";
    const currentVersion = VERSION_CONFIG.CURRENT_VERSION;

    const needsUpdate =
      forceUpdate || isVersionOutdated(currentVersion, minimumVersion);

    return {
      needsUpdate,
      forceUpdate,
      minimumVersion,
      currentVersion,
    };
  } catch (error) {
    // Silently fall back if Remote Config / Installations is not available
    // (e.g. project not configured, network offline, or unsupported environment)
    if (
      error?.code === "installations/request-failed" ||
      error?.code?.startsWith("installations/") ||
      error?.code?.startsWith("remote-config/")
    ) {
      console.warn(
        "Remote Config unavailable, skipping version check:",
        error.message,
      );
    } else {
      console.error("Error checking app version:", error);
    }
    return {
      needsUpdate: false,
      forceUpdate: false,
      minimumVersion: VERSION_CONFIG.FALLBACK_MINIMUM_VERSION,
      currentVersion: VERSION_CONFIG.CURRENT_VERSION,
    };
  }
}

/**
 * Check if support bypass is enabled (for testing/debugging)
 * @returns {boolean}
 */
export function isSupportBypassEnabled() {
  return localStorage.getItem(VERSION_CONFIG.STORAGE_KEYS.BYPASS) === "true";
}

/**
 * Enable support bypass (for testing/debugging)
 */
export function enableSupportBypass() {
  localStorage.setItem(VERSION_CONFIG.STORAGE_KEYS.BYPASS, "true");
  console.info("Version check bypass enabled");
}

/**
 * Disable support bypass
 */
export function disableSupportBypass() {
  localStorage.removeItem(VERSION_CONFIG.STORAGE_KEYS.BYPASS);
  console.info("Version check bypass disabled");
}
